import { ref, reactive, onMounted, computed, watch } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import Header from '@/components/HeaderDashboard.vue';
import Swal from 'sweetalert2';
import { hasPermission } from '@/store/userStore';
import { ClienteAlMayorService } from '@/services/ClienteAlMayorService';
import { MaestrosService } from '@/services/MaestrosService';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, UserIcon, PhoneIcon, MailIcon, PowerIcon, Loader2Icon } from 'lucide-vue-next';
export default {
    name: 'ClientesAlMayor',
    components: { Sidebar, Header, PlusIcon, EditIcon, TrashIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, UserIcon, PhoneIcon, MailIcon, PowerIcon, Loader2Icon },
    setup() {
        const clientes = ref([]);
        const isLoading = ref(false);
        const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        const searchQuery = ref('');
        const selectedLetter = ref(null);
        const currentPage = ref(1);
        const itemsPerPage = ref(8);
        const isModalOpen = ref(false);
        const isEditing = ref(false);
        const maestros = reactive({
            estados: [],
            municipios: [],
            parroquias: [],
            tiposDocumento: [],
            tiposContribuyente: [],
            tiposComercio: []
        });
        const form = ref({
            clienteId: 0,
            tipoDocumentoId: 0,
            documentoNumero: '',
            nombreRazonSocial: '',
            nombreFirmaPersonal: '',
            tipoContribuyenteId: 0,
            porcentajeRetencion: 0,
            direccion: '',
            estadoId: 0,
            municipioId: 0,
            parroquiaId: 0,
            sector: '',
            ciudad: '',
            telLocal1: '',
            telLocal2: '',
            telCelular1: '',
            telCelular2: '',
            email: '',
            tipoComercioId: 0,
            usuarioRegistro: 'Admin',
            active: true,
            telCelular1Prefix: '+58',
            telCelular1Number: '',
            telCelular2Prefix: '+58',
            telCelular2Number: '',
            telLocal1Prefix: '+58',
            telLocal1Number: '',
            telLocal2Prefix: '+58',
            telLocal2Number: ''
        });
        const formErrors = ref({
            tipoDocumentoId: false,
            tipoComercioId: false,
            tipoContribuyenteId: false,
            estadoId: false,
            municipioId: false,
            parroquiaId: false
        });
        const getPrefix = (id) => {
            const doc = maestros.tiposDocumento.find((d) => d.tipoDocumentoId === id);
            if (!doc)
                return '';
            const desc = doc.descripcion.toUpperCase();
            if (desc.includes('VENEZOLANO'))
                return 'V';
            if (desc.includes('EXTRANJERO'))
                return 'E';
            if (desc.includes('JURIDICO'))
                return 'J';
            if (desc.includes('RIF'))
                return 'RIF';
            if (desc.includes('PASAPORTE'))
                return 'P';
            const first = desc[0];
            if (['V', 'E', 'J', 'G', 'P'].includes(first))
                return first;
            return '';
        };
        const cleanDocumento = () => {
            const prefix = getPrefix(form.value.tipoDocumentoId);
            if (!prefix)
                return;
            const numbers = form.value.documentoNumero.split('-').pop()?.replace(/[^0-9]/g, '') || '';
            form.value.documentoNumero = `${prefix}-${numbers}`;
        };
        const updateDocumentoPrefix = () => {
            const prefix = getPrefix(form.value.tipoDocumentoId);
            if (!prefix)
                return;
            const numbers = form.value.documentoNumero.split('-').pop()?.replace(/[^0-9]/g, '') || '';
            form.value.documentoNumero = `${prefix}-${numbers}`;
        };
        const getDocumentoFormateado = (item) => {
            const prefix = getPrefix(item.tipoDocumentoId);
            return `${prefix}-${item.documentoNumero.replace(/[^0-9]/g, '')}`;
        };
        const totalCount = ref(0);
        const totalPages = ref(1);
        const loadData = async () => {
            isLoading.value = true;
            try {
                const [resCli, resEst, resDoc, resCont, resCom] = await Promise.all([
                    ClienteAlMayorService.getAll(currentPage.value, searchQuery.value, selectedLetter.value || ''),
                    MaestrosService.getEstados(),
                    MaestrosService.getTiposDocumento(),
                    MaestrosService.getTiposContribuyente(),
                    MaestrosService.getTiposComercio()
                ]);
                clientes.value = resCli.datos.items || [];
                totalCount.value = resCli.datos.totalCount || 0;
                totalPages.value = resCli.datos.totalPages || 1;
                maestros.estados = resEst.datos || [];
                maestros.tiposDocumento = resDoc.datos || [];
                maestros.tiposContribuyente = resCont.datos || [];
                maestros.tiposComercio = resCom.datos || [];
            }
            finally {
                isLoading.value = false;
            }
        };
        // Watchers para recargar datos cuando cambian filtros o pagina
        let searchTimeout;
        watch([currentPage, selectedLetter, searchQuery], (newValues, oldValues) => {
            // Check if searchQuery is what changed (it's at index 2)
            if (newValues[2] !== oldValues[2]) {
                isLoading.value = true;
                clearTimeout(searchTimeout);
                searchTimeout = setTimeout(() => {
                    currentPage.value = 1;
                    loadData();
                }, 500);
            }
            else {
                loadData();
            }
        });
        const onEstadoChange = async () => {
            form.value.municipioId = 0;
            form.value.parroquiaId = 0;
            const res = await MaestrosService.getMunicipiosByEstado(form.value.estadoId);
            maestros.municipios = res.datos || [];
        };
        const onMunicipioChange = async () => {
            form.value.parroquiaId = 0;
            const res = await MaestrosService.getParroquiasByMunicipio(form.value.municipioId);
            maestros.parroquias = res.datos || [];
        };
        const processedClientes = computed(() => {
            return clientes.value;
        });
        const splitPhone = (full) => {
            if (!full)
                return { prefix: '+58', number: '' };
            if (full.startsWith('+')) {
                const prefix = full.startsWith('+1') ? '+1' : '+58';
                return { prefix, number: full.replace(prefix, '') };
            }
            return { prefix: '+58', number: full };
        };
        const openModal = async (item = null) => {
            isEditing.value = !!item;
            if (item) {
                form.value = { ...item };
                // Desglosar teléfonos
                const cel1 = splitPhone(item.telCelular1);
                form.value.telCelular1Prefix = cel1.prefix;
                form.value.telCelular1Number = cel1.number;
                const cel2 = splitPhone(item.telCelular2);
                form.value.telCelular2Prefix = cel2.prefix;
                form.value.telCelular2Number = cel2.number;
                const loc1 = splitPhone(item.telLocal1);
                form.value.telLocal1Prefix = loc1.prefix;
                form.value.telLocal1Number = loc1.number;
                const loc2 = splitPhone(item.telLocal2);
                form.value.telLocal2Prefix = loc2.prefix;
                form.value.telLocal2Number = loc2.number;
                const [m, p] = await Promise.all([
                    MaestrosService.getMunicipiosByEstado(item.estadoId),
                    MaestrosService.getParroquiasByMunicipio(item.municipioId)
                ]);
                maestros.municipios = m.datos || [];
                maestros.parroquias = p.datos || [];
            }
            else {
                // Initialize clean form
                form.value = {
                    clienteId: 0,
                    documentoNumero: '',
                    nombreRazonSocial: '',
                    tipoDocumentoId: 0,
                    tipoContribuyenteId: 0,
                    direccion: '',
                    estadoId: 0,
                    municipioId: 0,
                    parroquiaId: 0,
                    sector: '',
                    ciudad: '',
                    telLocal1: '',
                    telLocal2: '',
                    telCelular1: '',
                    telCelular2: '',
                    email: '',
                    tipoComercioId: 0,
                    usuarioRegistro: 'Admin',
                    active: true,
                    telCelular1Prefix: '+58',
                    telCelular1Number: '',
                    telCelular2Prefix: '+58',
                    telCelular2Number: '',
                    telLocal1Prefix: '+58',
                    telLocal1Number: '',
                    telLocal2Prefix: '+58',
                    telLocal2Number: ''
                };
            }
            isModalOpen.value = true;
        };
        const saveCliente = async () => {
            formErrors.value.tipoDocumentoId = form.value.tipoDocumentoId === 0;
            formErrors.value.tipoComercioId = form.value.tipoComercioId === 0;
            formErrors.value.tipoContribuyenteId = form.value.tipoContribuyenteId === 0;
            formErrors.value.estadoId = form.value.estadoId === 0;
            formErrors.value.municipioId = form.value.municipioId === 0;
            formErrors.value.parroquiaId = form.value.parroquiaId === 0;
            if (Object.values(formErrors.value).some(v => v)) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Campos incompletos',
                    text: 'Debe seleccionar una opción en los campos marcados en rojo.',
                    confirmButtonColor: '#3b82f6'
                });
                return;
            }
            try {
                // Combinar teléfonos antes de enviar
                form.value.telCelular1 = `${form.value.telCelular1Prefix}${form.value.telCelular1Number.trim()}`;
                form.value.telCelular2 = `${form.value.telCelular2Prefix}${form.value.telCelular2Number.trim()}`;
                form.value.telLocal1 = `${form.value.telLocal1Prefix}${form.value.telLocal1Number.trim()}`;
                form.value.telLocal2 = `${form.value.telLocal2Prefix}${form.value.telLocal2Number.trim()}`;
                const res = isEditing.value
                    ? await ClienteAlMayorService.updateCliente(form.value.clienteId, form.value)
                    : await ClienteAlMayorService.addCliente(form.value);
                if (res.exito) {
                    Swal.fire('Éxito', 'Operación completada', 'success');
                    isModalOpen.value = false;
                    loadData();
                }
            }
            catch (e) {
                Swal.fire('Error', 'No se pudo guardar', 'error');
            }
        };
        const toggleActive = async (item) => {
            const result = await Swal.fire({
                title: '¿Cambiar estado del cliente?',
                text: `¿Estás seguro de que deseas ${item.active ? 'desactivar' : 'activar'} este cliente?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, cambiar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#94a3b8',
            });
            if (!result.isConfirmed)
                return;
            try {
                const res = await ClienteAlMayorService.toggleActivo(item.clienteId);
                if (res.exito) {
                    item.active = !item.active;
                    Swal.fire({
                        title: item.active ? 'Habilitado' : 'Deshabilitado',
                        text: `El cliente ahora está ${item.active ? 'activo' : 'inactivo'}.`,
                        icon: 'success',
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 2000
                    });
                }
            }
            catch (e) {
                Swal.fire('Error', 'No se pudo cambiar el estado', 'error');
            }
        };
        onMounted(loadData);
        return {
            hasPermission,
            clientes, isLoading, alphabet, searchQuery, selectedLetter, currentPage, maestros, form, formErrors, isModalOpen, isEditing,
            cleanDocumento, updateDocumentoPrefix, getDocumentoFormateado, toggleActive,
            totalCount,
            totalPages,
            paginatedClientes: computed(() => processedClientes.value),
            filterByLetter: (l) => { selectedLetter.value = l; currentPage.value = 1; },
            resetFilter: () => { selectedLetter.value = null; searchQuery.value = ''; currentPage.value = 1; },
            prevPage: () => { if (currentPage.value > 1)
                currentPage.value--; },
            nextPage: () => { if (currentPage.value < totalPages.value)
                currentPage.value++; },
            openModal, closeModal: () => isModalOpen.value = false, saveCliente,
            onEstadoChange, onMunicipioChange,
            placeholderDoc: computed(() => {
                const prefix = getPrefix(form.value.tipoDocumentoId);
                return prefix ? `Ej: ${prefix}-12345678` : 'Seleccione tipo primero...';
            }),
            confirmDelete: async (id) => {
                const res = await Swal.fire({
                    title: '¿Desea eliminar este cliente?',
                    text: "Esta acción no se puede deshacer",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3b82f6',
                    cancelButtonColor: '#94a3b8',
                    confirmButtonText: 'Sí, eliminar',
                    cancelButtonText: 'Cancelar'
                });
                if (res.isConfirmed) {
                    try {
                        const response = await ClienteAlMayorService.deleteCliente(id);
                        if (response.exito) {
                            Swal.fire({
                                title: '¡Eliminado!',
                                text: 'El cliente ha sido borrado correctamente.',
                                icon: 'success',
                                timer: 1500,
                                showConfirmButton: false
                            });
                            // Recargamos los datos
                            await loadData();
                        }
                        else {
                            Swal.fire('Error', response.errores || 'No se pudo eliminar el registro', 'error');
                        }
                    }
                    catch (error) {
                        Swal.fire('Error', 'Hubo un fallo de comunicación con el servidor', 'error');
                    }
                }
            }
        };
    }
};
